import { generateClient, GraphQLResult } from "aws-amplify/api";
// @ts-ignore
import { getFieldTemplate, getFormTemplate } from "../graphql/queries";
import { getFullFormTemplateOffline } from "../db/queries/formTemplateQueries";
import { recordSentry } from "./recordSentry";
import { FieldTemplate, FormTemplate, Option } from "db/definitions";
import batchGetOptionsByFormTemplate from "./batchGetOptionsByFormTemplate";

/**
 * Gets all field templates that belong to a specific FormTemplate
 * @param id for the form template by which to retrieve the field templates.
 * @param online Whether or not the user is online
 * @returns all the field templates.
 */
async function getFullFormTemplate(id: string, online: boolean): Promise<FormTemplate | void> {
	// CHANGING THIS PART TO GRAB ALL THE OPTIONS SEPARATELY BY USING THE FORM TEMPLATE PROPERTY
	// These will be mapped out and organized by field template, then assigned to the field template rather than fetching for each
	// field template
	if (online) {
		const formTemplateWithFields = await getBatch(id, null, []);
		const finalFields = [];
		const allOptionsInFormTemplate = await batchGetOptionsByFormTemplate(id, online);
		const optionMap: Map<string, Option[]> = new Map<string, Option[]>();
		// Now need to map out all these options, then pass that along to matchFieldWithAllOptions
		// map them out by fieldTemplateId - option[]
		for (let i = 0; i < allOptionsInFormTemplate.length; i++) {
			const option = allOptionsInFormTemplate[i];
			if (optionMap.has(option.fieldTemplateId)) {
				let newOptions = optionMap.get(option.fieldTemplateId);
				if (newOptions) {
					newOptions.push(option);
					optionMap.set(option.fieldTemplateId, newOptions);
				} else {
					optionMap.set(option.fieldTemplateId, [option]);
				}
			} else {
				optionMap.set(option.fieldTemplateId, [option]);
			}
		}

		if (formTemplateWithFields?.fieldTemplates) {
			for (const field of formTemplateWithFields.fieldTemplates.items) {
				const options = optionMap.get(field.id);
				if (options) {
					field.options = { items: options };
				}
				if (field.inputType === "table" && field.tableFields) {
					const finalTableFieldsArray = [];
					for (const tableField of field.tableFields.items) {
						const tableFieldOptions = optionMap.get(tableField.id);
						if (tableFieldOptions) {
							tableField.options = { items: tableFieldOptions };
						}
						finalTableFieldsArray.push(tableField);
					}

					field.tableFields.items = finalTableFieldsArray;
				}
				finalFields.push(field);
			}
			formTemplateWithFields.fieldTemplates.items = finalFields;
			return formTemplateWithFields;
		}
	} else {
		const fullOffline = await getFullFormTemplateOffline(id);
		return fullOffline;
	}
}

async function getBatch(formTemplateId: string, nextToken: string | null, prevItems: FieldTemplate[]): Promise<FormTemplate | void> {
	try {
		interface QueryResponse {
			getFormTemplate: FormTemplate;
		}
		const client = generateClient({ authMode: "userPool" });
		const result = (await client.graphql({
			query: getFormTemplate,
			variables: {
				nextToken: nextToken,
				id: formTemplateId,
			},
			authMode: "userPool",
		})) as unknown as GraphQLResult<QueryResponse>;
		const formTemplate = result.data.getFormTemplate;
		if (formTemplate.fieldTemplates) {
			for (const field of formTemplate.fieldTemplates.items) {
				prevItems.push(field);
			}
			if (formTemplate.fieldTemplates?.nextToken) {
				return getBatch(formTemplate.id, formTemplate.fieldTemplates.nextToken, prevItems);
			} else {
				formTemplate.fieldTemplates.items = prevItems;
				return formTemplate;
			}
		}
	} catch (error) {
		recordSentry({ error, msg: "Error getting full form template: " });
	}
}

export default getFullFormTemplate;
